import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Check, CheckCircle2, Eye, EyeOff, KeyRound, Loader2, Lock, Mail, ShieldCheck, User } from 'lucide-react';
import axios from 'axios';
import { API_BASE, errorText } from '../services/api';

/* Public page an invited team member opens from their link. Uses its own axios call (no auth header, and no
   redirect-to-sign-in on errors) since the visitor isn't signed in yet. */

interface InviteInfo { email: string; name: string | null; level: string; level_label: string; invited_by: string | null; expires_at: string }

const inputCls = 'w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 disabled:bg-slate-50 disabled:text-slate-500';

function Rule({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return <li className={`flex items-center gap-1.5 text-xs ${ok ? 'text-emerald-700' : 'text-slate-500'}`}>{ok ? <Check className="h-3.5 w-3.5" /> : <span className="mx-1 h-1 w-1 rounded-full bg-slate-300" />}{children}</li>;
}

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [show, setShow] = useState(false);
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setLoadError("This link is missing its invite code. Open the full link you were sent."); return; }
    axios.get(`${API_BASE}/invite`, { params: { token } })
      .then(({ data }) => { setInfo(data); setName(data.name || ''); })
      .catch(e => setLoadError(errorText(e, "This invite link isn't valid.")));
  }, [token]);

  const local = (info?.email || '').split('@')[0].toLowerCase();
  const checks = useMemo(() => ({
    length: password.length >= 12,
    mixed: /[a-z]/.test(password) && /[A-Z0-9]/.test(password),
    noEmail: !(local.length >= 4 && password.toLowerCase().includes(local)),
    match: !!password && password === password2,
    pin: /^\d{6}$/.test(pin) && new Set(pin).size > 1 && !['123456', '654321', '012345'].includes(pin),
    pinMatch: !!pin && pin === pin2,
  }), [password, password2, pin, pin2, local]);
  const ready = name.trim().length >= 2 && Object.values(checks).every(Boolean);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) { setError('Complete every field above first'); return; }
    setBusy(true); setError(null);
    try {
      await axios.post(`${API_BASE}/invite/accept`, { token, name: name.trim(), password, pin });
      setDone(true);
    } catch (err) { setError(errorText(err, "Couldn't create your account. Try again.")); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-[440px]">
          <div className="mb-7 flex flex-col items-center gap-3">
            <img src="/logo.png" alt="FluxWatch" className="h-14 w-auto" />
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
              <ShieldCheck className="h-3.5 w-3.5 text-red-600" /> Admin console
            </span>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
            {loadError ? (
              <div className="text-center">
                <span className="mx-auto mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600"><AlertTriangle className="h-5 w-5" /></span>
                <h1 className="text-lg font-semibold text-slate-900">This invite can't be used</h1>
                <p className="mt-2 text-sm text-slate-600">{loadError}</p>
                <Link to="/login" className="mt-5 inline-block text-sm font-medium text-slate-700 hover:text-slate-900">Go to sign in</Link>
              </div>
            ) : !info ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Checking your invite…</div>
            ) : done ? (
              <div className="text-center">
                <span className="mx-auto mb-3 inline-flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-6 w-6" /></span>
                <h1 className="text-lg font-semibold text-slate-900">You're in</h1>
                <p className="mt-2 text-sm text-slate-600">Your account for <b className="font-medium text-slate-900">{info.email}</b> is ready. Sign in with your password and PIN.</p>
                <Link to="/login" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800">
                  Go to sign in <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" noValidate>
                <div>
                  <h1 className="text-lg font-semibold text-slate-900">Join the FluxWatch admin team</h1>
                  <p className="mt-1 text-sm text-slate-500">
                    {info.invited_by ? <>{info.invited_by} invited you as </> : <>You're invited as </>}
                    <span className="font-medium text-slate-700">{info.level_label}</span>.
                  </p>
                </div>

                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium text-slate-600">Email</span>
                  <div className="relative"><Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input value={info.email} disabled className={inputCls} /></div>
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium text-slate-600">Your name</span>
                  <div className="relative"><User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input value={name} onChange={e => setName(e.target.value)} autoComplete="name" className={inputCls} /></div>
                </label>

                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-medium text-slate-600">Password</span>
                    <div className="relative"><Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input type={show ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" className={`${inputCls} pr-9`} />
                      <button type="button" tabIndex={-1} onClick={() => setShow(v => !v)} aria-label={show ? 'Hide password' : 'Show password'}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                    </div>
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-medium text-slate-600">Repeat password</span>
                    <div className="relative"><Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input type={show ? 'text' : 'password'} value={password2} onChange={e => setPassword2(e.target.value)} autoComplete="new-password" className={inputCls} /></div>
                  </label>
                </div>
                <ul className="grid grid-cols-2 gap-1">
                  <Rule ok={checks.length}>12+ characters</Rule>
                  <Rule ok={checks.mixed}>Letters plus a capital or number</Rule>
                  <Rule ok={checks.noEmail}>Doesn't contain your email name</Rule>
                  <Rule ok={checks.match}>Both passwords match</Rule>
                </ul>

                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-medium text-slate-600">6-digit PIN</span>
                    <div className="relative"><KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input type="password" inputMode="numeric" maxLength={6} value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} autoComplete="off" className={`${inputCls} tracking-[0.3em]`} /></div>
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-medium text-slate-600">Repeat PIN</span>
                    <div className="relative"><KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input type="password" inputMode="numeric" maxLength={6} value={pin2} onChange={e => setPin2(e.target.value.replace(/\D/g, ''))} autoComplete="off" className={`${inputCls} tracking-[0.3em]`} /></div>
                  </label>
                </div>
                <ul className="grid grid-cols-2 gap-1">
                  <Rule ok={checks.pin}>Not repeated or sequential</Rule>
                  <Rule ok={checks.pinMatch}>Both PINs match</Rule>
                </ul>

                {error && <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{error}</div>}

                <button type="submit" disabled={busy || !ready}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create my account <ArrowRight className="h-4 w-4" /></>}
                </button>
                <p className="text-center text-xs text-slate-500">You'll sign in with this password and PIN every time.</p>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
